import { take, timer } from "rxjs";
import { GlobalService } from "./global.service";
import { FieldData, Orgfield, Personfield, PhoneValue, Recipient } from "app/main/pages/edit-template/edit-template.dto";
import { SelectedFieldEnum } from "app/interfaces/fields.dto";
import { generateCalculation } from "./calculation";
import { applyRelations } from "./fieldrelation";


/**
 * Common helper: get matching field UUIDs from globalService.fields_data
 */
function getMatchingUuids( fields_data: FieldData, filter: Partial<Record<keyof FieldData[string], unknown>>): string[] {
  return Object.keys(fields_data).filter((uuid) =>
    Object.entries(filter).every(([key, value]) => fields_data[uuid][key] == value)
  );
}

/**
 * Common helper: update DOM + service for a given field
 */
export function applyValueToField( uuid: string, value: string, globalService: GlobalService, isRequired: boolean = false) {
    const dragField = document.querySelector(`.drag-fields[uuid="${uuid}"]`) as HTMLElement;
    if(dragField){
        const inputs = dragField.querySelectorAll('input, textarea') as NodeListOf<HTMLInputElement | HTMLTextAreaElement>;
        inputs.forEach(input => {
            input.setAttribute('value', value);
            input.value = value;
        });
        if(dragField.querySelector('textarea')){
            (dragField.querySelector('textarea') as HTMLTextAreaElement).textContent = value;
        }
        if(dragField?.querySelector('.text-tag')){
            dragField.querySelector('.text-tag').textContent = (value === '' ? globalService.generateTagText(uuid) : value);
        }
        globalService.updateField(uuid, { value });
        if(isRequired){
            inputs.forEach(input => {
                if (input.classList.contains("calculation")) {
                    generateCalculation(uuid, globalService, true);
                }
            });
            applyRelations(uuid, globalService);
        }
    }
}

/**
 * On connect recipient 
 * @param field 
 * @param recipient_id 
 */
export function updatePersonField(field: Personfield, recipient_id: number, selected_for_role_id: string|number, globalService: GlobalService, field_section: string = 'person_field', recipient:Recipient) {
    const fields_data:FieldData = globalService.fields_data;

    const uuids = getMatchingUuids(fields_data, {
        selected_field_role_id: selected_for_role_id,
        selected_field_type_id: field.id,
        field_section
    });

    if(field.key === 'first_name'){
        field.value = `${recipient.firstname} ${recipient.lastname ?? ''}`
    }
    if (field.key === 'phone_no') {
        const phoneValue = field.value as PhoneValue | string | undefined;
        if (typeof phoneValue === 'string') {
            field.value = phoneValue;
        } else {
            const e164Number = phoneValue?.e164Number;
            if (typeof e164Number === 'string') {
                field.value = e164Number;
            } else if (phoneValue && typeof phoneValue === 'object') {
                field.value = String((phoneValue as PhoneValue).number ?? '');
            } else {
                field.value = '';
            }
        }
    }
    uuids.forEach(uuid => {
        globalService.updateField(uuid, { value: field.value, recipient_id: recipient_id, lock: (field.lock ? 1 : 0) });
        applyValueToField(uuid, field.value, globalService, true);
    });
}

export function updateSignatureBox(selected_for_role_id: string | number, globalService: GlobalService, recipient: Recipient, roleName = "") {
  const uuids = getMatchingUuids(globalService.fields_data, {
    selected_for_role_id,
    selected_field: SelectedFieldEnum.SignatureField
  });

  uuids.forEach((uuid) => {
    const dragField = document.querySelector(`.drag-fields[uuid="${uuid}"]`) as HTMLElement;
    if (dragField) {
      dragField.querySelector(".assignee").textContent = getFullName(recipient, roleName) || "unknown";
    }
  });
}


/**
 * Reset recipient fields
 * @param field 
 * @param recipient_id 
 */
export function resetPersonFields(field: Personfield, selected_field_role_id: string|number, globalService: GlobalService, field_section: string = 'person_field', isRequired:boolean = true) {

    const fields_data:FieldData = globalService.fields_data;
    const uuids = getMatchingUuids(fields_data, {
        selected_field_role_id,
        selected_field_type_id: field.id,
        field_section
    });
    uuids.forEach(uuid => {
        globalService.updateField(uuid, { value: '', recipient_id: 0, organization_id: 0 });
        applyValueToField(uuid, "", globalService, isRequired);
    })
}


/**
 * On connect organization then patch that organization values to tag
 * @param field 
 * @param recipient_id 
 */
export function updateOrganizationField(field: Orgfield, recipient_id: number, selected_for_role_id: string|number, globalService: GlobalService, field_section: string = 'org_field', isEForm:boolean = false) {
    const fields_data:FieldData = globalService.fields_data;
    if (!(recipient_id > 0 || isEForm)) return;
    const uuids = getMatchingUuids(fields_data, {
        selected_field_role_id: selected_for_role_id,
        selected_field_type_id: field.id,
        field_section
    });
    uuids.forEach((uuid) => {
        globalService.updateField(uuid, { value: field.value, recipient_id: recipient_id, lock: (field.lock ? 1 : 0) });
        applyValueToField(uuid, String(field.value), globalService, true);
    })
}



/**
 * Reset organization tags
 * @param field 
 * @param recipient_id 
 */
export function resetOrganizationFields(field: Orgfield, selected_field_role_id: number, globalService: GlobalService, field_section: string = 'org_field', isRequired:boolean = true) {
    const fields_data:FieldData = globalService.fields_data;
    const uuids = getMatchingUuids(fields_data, {
        selected_field_role_id,
        selected_field_type_id: field.id,
        field_section
    });
    uuids.forEach((uuid) => {
        globalService.updateField(uuid, { value: '', recipient_id: 0, organization_id: 0 });
        applyValueToField(uuid, "", globalService, isRequired);
    });
}




/**
 * Update field value on keydown
 * @param $event 
 * @param field 
 * @returns 
 */
export function updateFieldValue(recipient_id: number, field: {value:Personfield|Orgfield}, globalService: GlobalService, field_section: string) {
    const fields_data:FieldData = globalService.fields_data;
    if (recipient_id <= 0) return;
    const uuids = getMatchingUuids(fields_data, {
        recipient_id,
        selected_field_type_id: field.value.id,
        field_section
    });
    timer(0, 990)
    .pipe(take(1))
    .subscribe(() => {
      uuids.forEach((uuid) => {
        globalService.updateField(uuid, { value: field.value.value, lock: (field.value.lock ? 1 : 0) });
        applyValueToField(uuid, String(field.value.value), globalService, true);
      })
    })
}

export function updateValue(uuid, value, globalService){
    globalService.updateField(uuid, { value: value });
    applyValueToField(uuid, value, globalService);
}

/**
 * reset all the related role fields
 * @param field 
 * @param selected_field_role_id 
 * @param globalService 
 * @param field_section 
 */
export function resetFieldsOnDeleteRole(field: Personfield|Orgfield, selected_field_role_id: string, globalService: GlobalService, field_section: string) {
    const fields_data = globalService.fields_data;
    const uuids = getMatchingUuids(fields_data, {
        selected_field_role_id,
        prefixid: field.id,
        field_section
    });
    uuids.forEach((uuid) => {
        globalService.updateField(uuid, { value: '', recipient_id: "0", organization_id: "0", selected_field_type_id: "0", selected_field_role_id: "0", selected_for_role_id: "0", role_name: 'Sender' });
        applyValueToField(uuid, "", globalService, true);
    });
    updateSignatureBox(selected_field_role_id, globalService, null);
}

const getFullName = (recipient: Recipient, roleName:string) => ((recipient && recipient?.firstname ? `${recipient?.firstname ?? ''} ${recipient?.lastname ?? ''}` : roleName));